import { Injectable } from "@angular/core";
import { Portfolio } from "../models/portfolio";
import { Subject, map, Observable, of, take, shareReplay, switchMap, forkJoin, tap } from "rxjs";
import { AuthenticationService } from "./AuthenticationService";
import { ApplicationUser } from "../models/applicationUser";
import { PropertyDataApiService } from "./PropertyDataApiService";
import { PropertyService } from "./PropertyService";
import { PortfoliosResponse } from "../responses/portfoliosResponse";
import { MatSnackBar } from "@angular/material/snack-bar";
import { PropertyAbstract } from "../models/propertyAbstract";
import { PortfolioPublishingData } from "../models/portfolioPublishingData";
import { PortfolioDTO } from "../DTO/portfolioDTO";

@Injectable({ providedIn: 'root' })
export class PortfolioService{
    private portfolioObservableCache: Observable<Portfolio[]> | undefined;
    private portfolioCache: Map<number, Portfolio> = new Map<number, Portfolio>();
    public portfolioCacheChangedSubject = new Subject<void>();
    
    constructor(private authService:AuthenticationService, private pdApiService:PropertyDataApiService, private propertyService:PropertyService, private snackBar: MatSnackBar){
        this.authService.currentUserSubject.subscribe(au=>this.processCurrentUser(au));
    }

    get authorized(){
        return this.authService.authorizedSubject.value;
    }

    private processCurrentUser(au:ApplicationUser | undefined){
        if(!au){
            //user logged out, clear all personal caches
            this.clearPersonalCache();
        }
    }

    private clearPersonalCache(){
        this.portfolioCache = new Map<number, Portfolio>();
        this.portfolioObservableCache=undefined;
    }

    // Get all portfolios
    public getPortfolios() {
        if (!this.authorized) return of([]);
        if (this.portfolioCache.size > 0) return of([...this.portfolioCache.values()].sort((a, b) => a.name.localeCompare(b.name)));
        if (this.portfolioObservableCache) return this.portfolioObservableCache;

        this.portfolioObservableCache = this.pdApiService.getPortfolios().pipe(
            switchMap(data => {
                if (!data.success) return of([]);

                const portfolios = data.portfolios.map((dto:PortfolioDTO) => {
                    const portfolio = Portfolio.mapFromDTO(dto);
                    this.portfolioCache.set(portfolio.id!, portfolio);
                    return portfolio;
                });

                return this.relatePropertiesToPortfolios(portfolios);
            }),
            map(portfolios => portfolios.sort((a, b) => a.name.localeCompare(b.name))),
            shareReplay(1)
        );

        return this.portfolioObservableCache;
    }

    private relatePropertiesToPortfolios(portfolios: Portfolio[]): Observable<Portfolio[]> {
        // Collect all unique property IDs across all portfolios
        const allPropertyIds = [...new Set<number>(portfolios.flatMap(p => p.propertyIdList))];

        if (allPropertyIds.length === 0) return of(portfolios);

        // Fetch all unique properties in parallel
        return forkJoin(
            Object.fromEntries(allPropertyIds.map(pid => [pid, this.propertyService.getPropertyAbstractById(pid).pipe(take(1))]))
        ).pipe(
            map(propertiesById => {
                portfolios.forEach(portfolio => {
                    portfolio.propertyIdList.forEach(pid => {
                        const prop = propertiesById[pid];
                        if (prop) portfolio.addProperty(prop);
                    });
                });
                return portfolios;
            })
        );
    }

    // Get a specific portfolio by id
    public getPortfolioById(id: number, forceReload: boolean): Observable<Portfolio | undefined> {
        if (this.portfolioCache.size > 0) {
            if (!forceReload) {
                const p = this.portfolioCache.get(id);
                if (p) return of(p);
            }

            return this.pdApiService.getPortfolioById(id).pipe(
                take(1),
                switchMap((r: PortfoliosResponse) => {
                    if (!r.success) return of(undefined);
                    const portfolios = r.portfolios.map(dto => {
                        const portfolio = Portfolio.mapFromDTO(dto);
                        this.portfolioCache.set(portfolio.id!, portfolio);
                        return portfolio;
                    });
                    return this.relatePropertiesToPortfolios(portfolios).pipe(
                        map(() => this.portfolioCache.get(id))
                    );
                })
            );
        } else {
            return this.getPortfolios().pipe(
                take(1),
                map(() => this.portfolioCache.get(id))
            );
        }
    }

    // Add/Update a portfolio
    public storePortfolio(portfolio: Portfolio) {
        return this.pdApiService.storePortfolio(portfolio).pipe(take(1), map(r=>{
            if(r.success){
                portfolio = Portfolio.mapFromDTO(r.portfolio);
                this.portfolioCache.set(portfolio.id!, portfolio);
                this.portfolioCacheChangedSubject.next();
                this.snackBar.open('Portfolio stored', '×', { panelClass: ['primary-snackbar'], verticalPosition: 'top', duration:3000});
            }
            else{
                this.snackBar.open('Error deleting Portfolio: ' + r.message, '×', { panelClass: ['warn-snackbar'], verticalPosition: 'top'});
            }
            return r;
        }));
    }

    // Delete a portfolio
    public deletePortfolio(portfolio: Portfolio) {
        return this.pdApiService.deletePortfolio(portfolio).pipe(take(1), map(r=>{
            if(r.success){
                this.removePortfolioFromCache(portfolio.id!);
                this.snackBar.open('Portfolio deleted', '×', { panelClass: ['primary-snackbar'], verticalPosition: 'top', duration:3000});
            }
            else{
                this.snackBar.open('Error deleting Portfolio: ' + r.message, '×', { panelClass: ['warn-snackbar'], verticalPosition: 'top'});
            }
            return r;
        }));
    }

    public removePortfolioFromCache(portfolioId: number){
        this.portfolioCache.delete(portfolioId);
        this.portfolioCacheChangedSubject.next();
    }

    //triggered by signalR message
    public processPortfolioUpdate(ppd: PortfolioPublishingData): void {
        if(ppd.userId != this.authService.currentUserValue!.id){
            //another user changed portfolio relation => update; same user is already up to date.
            const propertyUpdate$ = ppd.propertyId ? this.propertyService.getPropertyAbstractById(ppd.propertyId).pipe(take(1), tap(pa => {
                if(pa){    
                    if (ppd.propertyAdded) {
                        pa.portfolioId = ppd.id;
                    } 
                    else if (pa.portfolioId === ppd.id) {
                        // remove only if it is the same id, since there could be a race condition between "removed from" and "added to" portfolio events
                        pa.portfolioId = undefined;
                    }
                }})
            ) : of(undefined);
            propertyUpdate$.pipe(switchMap(() => this.getPortfolioById(ppd.id, true)), take(1)).subscribe();
        }
    }

    // Find all portfolios that match the given property
    public findMatchingPortfolios(propertyTags:Map<string,string>) {
        return this.getPortfolios().pipe(take(1), map(pc => {
            const matches: Portfolio[] = [];
            pc.forEach(portfolio => {
                if (portfolio.findMatchingPortfolio(propertyTags)) {
                    matches.push(portfolio);
                }
            });
            return matches;
        }));
    }

    // Find portfolios matching a specific tag name and value
    public findPortfoliosByTag(tagName: string, tagValue: string){
        return this.getPortfolios().pipe(take(1), map(pc => {
            const matches: Portfolio[] = [];
            pc.forEach(portfolio => {
                const tagValues = portfolio.tags.get(tagName);
                if (tagValues && tagValues.includes(tagValue)) {
                    matches.push(portfolio);
                }
            });
            return matches;
        }));
    }

    //add/remove property to/from portfolio
    public storePortfolioProperty(old_portfolioId: number|undefined, new_portfolioId:number|undefined, pa:PropertyAbstract, reason:string){
        return this.pdApiService.storePortfolioProperty(new_portfolioId,pa.id,reason).pipe(take(1), map(r=>{
            if(r.success){
                if(old_portfolioId){
                    this.portfolioCache.get(old_portfolioId)?.removeProperty(pa);
                }
                if(new_portfolioId){
                    this.portfolioCache.get(new_portfolioId)?.addProperty(pa);
                }
                pa.portfolioId=new_portfolioId;
                pa.portfolioPropertyCount=new_portfolioId?this.portfolioCache.get(new_portfolioId)?.propertyAbstracts.length:0;
                this.snackBar.open('Property portfolio changed', '×', { panelClass: ['primary-snackbar'], verticalPosition: 'top', duration:3000});
            }
            else{
                this.snackBar.open('Error changing property ortfolio: ' + r.message, '×', { panelClass: ['warn-snackbar'], verticalPosition: 'top'});
            }
            return r;
        }));
    }


    // Get all unique tag values across all portfolios for a given tag name
    public getAllTagValues(tagName: string){
        return this.getPortfolios().pipe(take(1), map(pc => {
            const allValues = new Set<string>();
            pc.forEach(portfolio => {
                const tagValues = portfolio.tags.get(tagName);
                if (tagValues) {
                    tagValues.forEach(value => allValues.add(value));
                }
            });
            return Array.from(allValues);
        }));
    }

    public getUnusedPortfolioName(){
        const prefix = 'Portfolio';
        let suffix = 1;
        const p_array = [...this.portfolioCache.values()];
        while(p_array.some(p => p.name === `${prefix} ${suffix}`)) {
            suffix++;
        }
        return `${prefix} ${suffix}`;
    }

    public portfolioNameExists(name: string, excludeId?: number){      
        return [...this.portfolioCache.values()].some(p => p.name === name && (excludeId === undefined || p.id !== excludeId));
    }
}